import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect, useParams } from "react-router-dom";
import axios from "axios";
import Iframe from "react-iframe";
import {
  FaCalendarAlt,
  FaChartPie,
  FaCheck,
  FaExchangeAlt,
  FaFileAlt,
  FaMoneyBillWave,
  FaSyncAlt,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Must match `document_type` in `tm_printable_sample` (getPrintableSample.php). */
const DEFAULT_DOCUMENT_TYPE = "Statement";

const FALLBACK_PRINTABLE = {
  sample_id: "",
  sample_name: "Cash Statement",
  file_directory: `${process.env.REACT_APP_TARGET_DIR || ""}Printables/Report/`,
  file_name: "statement_report.php",
};

/** Date range quick presets — same set as CollectionReport.js */
const DATE_PRESETS = [
  { value: "today", label: "Today", icon: "fa-calendar-day", color: "#10b981" },
  { value: "yesterday", label: "Yesterday", icon: "fa-calendar-minus", color: "#6366f1" },
  { value: "thisWeek", label: "This Week", icon: "fa-calendar-week", color: "#3b82f6" },
  { value: "lastWeek", label: "Last Week", icon: "fa-calendar-week", color: "#a855f7" },
  { value: "last7Days", label: "Last 7 Days", icon: "fa-calendar", color: "#8b5cf6" },
  { value: "thisMonth", label: "This Month", icon: "fa-calendar-alt", color: "#f59e0b" },
  { value: "lastMonth", label: "Last Month", icon: "fa-calendar-check", color: "#ef4444" },
  { value: "last30Days", label: "Last 30 Days", icon: "fa-calendar", color: "#06b6d4" },
];

//Created By Cursor
function sampleDisplayName(sample, index = 0) {
  const fileName = String(sample?.file_name || "").toLowerCase();
  const raw = String(sample?.sample_name || "").trim();
  if (
    /statement_report_datewise\.php$/i.test(fileName) ||
    /^expense list\s*\(date wise\)$/i.test(raw)
  ) {
    return "Statement (Date Wise)";
  }
  return raw || sample?.file_name || `Sample ${index + 1}`;
}

const REPORT_TYPES = [
  { value: "today", label: "Today", icon: "fa-calendar-day", color: "#10b981" },
  { value: "asOnDate", label: "As On Date", icon: "fa-history", color: "#06b6d4" },
  { value: "dateRange", label: "Date Range", icon: "fa-calendar-alt", color: "#3b82f6" },
  { value: "month", label: "Month Wise", icon: "fa-calendar", color: "#8b5cf6" },
];

//Created By Cursor
function formatCurrency(amount) {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

//Created By Cursor
function formatDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

//Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

//Created By Cursor
function buildSessionMonths(startRaw, endRaw) {
  const start = parseLooseDate(startRaw);
  const end = parseLooseDate(endRaw);
  if (!start || !end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${y}-${m}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

//Created By Cursor
function monthBounds(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return { from: "", to: "" };
  const lastDay = new Date(+m[1], +m[2], 0).getDate();
  return {
    from: `${m[1]}-${m[2]}-01`,
    to: `${m[1]}-${m[2]}-${String(lastDay).padStart(2, "0")}`,
  };
}

//Created By Cursor
function formatDisplayDate(raw) {
  const d = parseLooseDate(raw);
  if (!d) return raw || "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const EMPTY_SUMMARY = {
  collection_total: 0,
  collection_count: 0,
  cash_in_total: 0,
  cash_in_count: 0,
  cash_out_total: 0,
  cash_out_count: 0,
  payroll_total: 0,
  payroll_count: 0,
  inflow_total: 0,
  outflow_total: 0,
  net_total: 0,
};

export default function StatementManager() {
  const loggedIn = localStorage.getItem("token") != null;
  const { document_type: routeDocType } = useParams();
  const documentType = routeDocType || DEFAULT_DOCUMENT_TYPE;

  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStartDate, setSessionStartDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState("");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);

  const [reportType, setReportType] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");

  const [samples, setSamples] = useState([FALLBACK_PRINTABLE]);
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [samplesLoaded, setSamplesLoaded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [fileUrl, setFileUrl] = useState("");
  const [iframeLoading, setIframeLoading] = useState(false);
  const [height, setHeight] = useState("520px");
  const iframeRef = useRef(null);
  const selectedSampleRef = useRef(FALLBACK_PRINTABLE);

  const monthOptions = useMemo(
    () => buildSessionMonths(sessionStartDate, sessionEndDate),
    [sessionStartDate, sessionEndDate]
  );

  const currentYm = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const selectedSample = useMemo(() => {
    if (!samples.length) return FALLBACK_PRINTABLE;
    return (
      samples.find((s) => String(s.sample_id) === String(selectedSampleId)) ||
      samples[0] ||
      FALLBACK_PRINTABLE
    );
  }, [samples, selectedSampleId]);

  selectedSampleRef.current = selectedSample;

  //Created By Cursor — same presets as CollectionReport.js
  const handleDateRangePreset = useCallback((preset) => {
    const today = new Date();
    let newStartDate = "";
    let newEndDate = "";

    switch (preset) {
      case "today":
        newStartDate = newEndDate = formatDateLocal(today);
        break;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        newStartDate = newEndDate = formatDateLocal(yesterday);
        break;
      }
      case "thisWeek": {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        newStartDate = formatDateLocal(startOfWeek);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "lastWeek": {
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        newStartDate = formatDateLocal(lastWeekStart);
        newEndDate = formatDateLocal(lastWeekEnd);
        break;
      }
      case "last7Days": {
        const last7Days = new Date(today);
        last7Days.setDate(today.getDate() - 7);
        newStartDate = formatDateLocal(last7Days);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "thisMonth": {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        newStartDate = formatDateLocal(startOfMonth);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "lastMonth": {
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        newStartDate = formatDateLocal(firstDayLastMonth);
        newEndDate = formatDateLocal(lastDayLastMonth);
        break;
      }
      case "last30Days": {
        const last30Days = new Date(today);
        last30Days.setDate(today.getDate() - 30);
        newStartDate = formatDateLocal(last30Days);
        newEndDate = formatDateLocal(today);
        break;
      }
      default:
        return;
    }

    setDateRangePreset(preset);
    setDateFrom(newStartDate);
    setDateTo(newEndDate);
  }, []);

  //Created By Cursor
  const handleCalendarDateClick = useCallback(
    (date) => {
      const dateStr = formatDateLocal(date);
      if (selectingStartDate || !dateFrom || dateStr < dateFrom) {
        setDateFrom(dateStr);
        setDateTo("");
        setSelectingStartDate(false);
        setDateRangePreset("custom");
      } else {
        setDateTo(dateStr);
        setSelectingStartDate(true);
        setDateRangePreset("custom");
      }
    },
    [selectingStartDate, dateFrom]
  );

  //Created By Cursor
  const navigateCalendarMonth = useCallback((direction) => {
    setCalendarMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + direction);
      return next;
    });
  }, []);

  const generateCalendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let day = 1; day <= lastDay; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  }, [calendarMonth]);

  //Created By Cursor
  const isDateInRange = useCallback(
    (date) => {
      if (!dateFrom || !dateTo) return false;
      const dateStr = formatDateLocal(date);
      return dateStr >= dateFrom && dateStr <= dateTo;
    },
    [dateFrom, dateTo]
  );

  //Created By Cursor
  const isDateSelected = useCallback(
    (date) => {
      const dateStr = formatDateLocal(date);
      return dateStr === dateFrom || dateStr === dateTo;
    },
    [dateFrom, dateTo]
  );

  //Created By Cursor
  const isTodayDate = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  //Created By Cursor
  const resolveBounds = useCallback(() => {
    if (reportType === "today") {
      const today = formatDateLocal(new Date());
      return { from: today, to: today };
    }
    if (reportType === "asOnDate") {
      const today = formatDateLocal(new Date());
      const start = parseLooseDate(sessionStartDate);
      const from = start ? formatDateLocal(start) : today;
      return { from, to: today };
    }
    if (reportType === "month") {
      return monthBounds(selectedMonth);
    }
    return { from: dateFrom || "", to: dateTo || "" };
  }, [reportType, selectedMonth, dateFrom, dateTo, sessionStartDate]);

  //Created By Cursor
  const buildFileUrl = useCallback(
    (sample, sid, from, to, rType, monthYm) => {
      if (!sid) return "";
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return "";
      if (!from || !to) return "";

      let directory = String(
        sample?.file_directory || FALLBACK_PRINTABLE.file_directory || ""
      ).trim();
      let fileName = String(
        sample?.file_name || FALLBACK_PRINTABLE.file_name || ""
      ).trim();

      if (!fileName) {
        fileName = FALLBACK_PRINTABLE.file_name;
      }
      if (!directory) {
        directory = FALLBACK_PRINTABLE.file_directory;
      }
      if (directory && !directory.endsWith("/")) directory += "/";

      const params = new URLSearchParams();
      params.set("session_id", sid);
      params.set("school_code", schoolCode);
      params.set("report_type", rType || "today");
      params.set("date_from", from);
      params.set("date_to", to);
      params.set("document_type", documentType);
      if (rType === "month" && monthYm) params.set("year_month", monthYm);
      if (sample?.sample_id) params.set("sample_id", sample.sample_id);
      if (process.env.REACT_APP_SYSTEM_CODE) {
        params.set("system_code", process.env.REACT_APP_SYSTEM_CODE);
      }

      return `${process.env.REACT_APP_SERVER_NAME}${directory}${fileName}?${params.toString()}`;
    },
    [documentType]
  );

  //Created By Cursor
  const refreshPreview = useCallback(
    (sample, sid, from, to, rType, monthYm) => {
      const url = buildFileUrl(sample, sid, from, to, rType, monthYm);
      console.log("[StatementManager] FULL URL>>>", url || "(empty)");
      if (!url) {
        setFileUrl("");
        setIframeLoading(false);
        return;
      }
      setIframeLoading(true);
      setHeight("520px");
      setFileUrl(url);
    },
    [buildFileUrl]
  );

  //Created By Cursor
  const fetchSamples = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setSamples([FALLBACK_PRINTABLE]);
      setSelectedSampleId("");
      setSamplesLoaded(true);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE()}getPrintableSample.php`, {
        params: {
          school_code: schoolCode,
          document_type: documentType,
        },
        timeout: 30000,
      });
      const nextSamples =
        res.data?.api_status === "OK" &&
        Array.isArray(res.data.data) &&
        res.data.data.length
          ? res.data.data
          : [FALLBACK_PRINTABLE];
      setSamples(nextSamples);
      setSelectedSampleId((prev) => {
        const keep = nextSamples.find(
          (s) => String(s.sample_id) === String(prev)
        );
        if (keep?.sample_id) return String(keep.sample_id);
        return nextSamples[0]?.sample_id
          ? String(nextSamples[0].sample_id)
          : "";
      });
    } catch (err) {
      console.error("[StatementManager] sample fetch error", err);
      setSamples([FALLBACK_PRINTABLE]);
      setSelectedSampleId("");
    } finally {
      setSamplesLoaded(true);
    }
  }, [documentType]);

  //Created By Cursor
  const fetchStatement = useCallback(
    async ({ soft = false, sample } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!sessionId || !schoolCode) return;

      const bounds = resolveBounds();
      if (!bounds.from || !bounds.to) {
        myToast.notify_warning("Select a valid date range or month.");
        return;
      }

      const activeSample = sample || selectedSampleRef.current;

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API_BASE()}getStatementSummary.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            date_from: bounds.from,
            date_to: bounds.to,
            detail_limit: 1,
          },
          timeout: 45000,
        });
        if (res.data?.api_status === "OK") {
          const payload = res.data.data || {};
          setSummary({ ...EMPTY_SUMMARY, ...(payload.summary || {}) });
        } else {
          setSummary(EMPTY_SUMMARY);
          myToast.notify_warning(
            res.data?.api_remark || "Could not load statement."
          );
        }
        refreshPreview(
          activeSample,
          sessionId,
          bounds.from,
          bounds.to,
          reportType,
          selectedMonth
        );
      } catch (err) {
        console.error("[StatementManager] fetch error", err);
        myToast.notify_warning("Failed to load statement.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId, resolveBounds, refreshPreview, reportType, selectedMonth]
  );

  //Created By Cursor
  const handleSampleSelect = useCallback(
    (sample) => {
      if (!sample) return;
      const nextId = String(sample.sample_id || "");
      setSelectedSampleId(nextId);
      const bounds = resolveBounds();
      if (!sessionId || !bounds.from || !bounds.to) return;
      refreshPreview(
        sample,
        sessionId,
        bounds.from,
        bounds.to,
        reportType,
        selectedMonth
      );
    },
    [resolveBounds, sessionId, refreshPreview, reportType, selectedMonth]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    if (Array.isArray(mSession) && mSession.length > 0) {
      setSessionId(mSession[0].session_id);
      setSessionName(mSession[0].session_name);
      setSessionStartDate(mSession[0].start_date || "");
      setSessionEndDate(mSession[0].end_date || "");
      const months = buildSessionMonths(
        mSession[0].start_date || "",
        mSession[0].end_date || ""
      );
      const now = new Date();
      const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      setSelectedMonth(months.includes(cur) ? cur : months[months.length - 1] || "");
    }
    fetchSamples();
  }, [fetchSamples]);

  useEffect(() => {
    if (reportType === "dateRange" && !dateRangePreset) {
      handleDateRangePreset("today");
    }
  }, [reportType, dateRangePreset, handleDateRangePreset]);

  useEffect(() => {
    if (!sessionId || !samplesLoaded) return;
    if (reportType === "month" && !selectedMonth) return;
    if (reportType === "dateRange" && (!dateFrom || !dateTo)) return;
    fetchStatement();
  }, [
    sessionId,
    samplesLoaded,
    reportType,
    selectedMonth,
    dateFrom,
    dateTo,
    fetchStatement,
  ]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const periodLabel = useMemo(() => {
    const b = resolveBounds();
    if (!b.from) return "—";
    if (b.from === b.to) return formatDisplayDate(b.from);
    return `${formatDisplayDate(b.from)} – ${formatDisplayDate(b.to)}`;
  }, [resolveBounds]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page smhub-page stmt-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Cash Statement</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell smhub-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaChartPie />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Cash Statement</h1>
              <p className="sm-hero__sub">
                Collection · Expense · Payroll · {periodLabel}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="smhub-btn-new stmt-session-btn"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change session"
            >
              <span className="smhub-btn-new__icon" aria-hidden>
                <FaExchangeAlt />
              </span>
              <span className="smhub-btn-new__label">
                {sessionName || "Session"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => fetchStatement({ soft: true })}
              disabled={loading || refreshing || !sessionId}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh statement"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="smhub-body stmt-body">
          <section className="stmt-controls" aria-label="Statement period">
            <div className="stmt-period" role="tablist" aria-label="Period type">
              {REPORT_TYPES.map((t) => {
                const isActive = reportType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`stmt-period__card${isActive ? " is-active" : ""}`}
                    style={{
                      "--period-color": t.color,
                      ...(isActive
                        ? {
                            background: `linear-gradient(135deg, ${t.color}15 0%, ${t.color}08 100%)`,
                            boxShadow: `0 4px 12px ${t.color}30, 0 2px 4px rgba(0, 0, 0, 0.1)`,
                          }
                        : null),
                    }}
                    onClick={() => setReportType(t.value)}
                  >
                    <span className="stmt-period__icon-wrap" aria-hidden>
                      <i className={`fa ${t.icon}`} />
                    </span>
                    <span className="stmt-period__label">{t.label}</span>
                    {isActive ? (
                      <i
                        className="fa fa-check-circle stmt-period__check"
                        aria-hidden
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {reportType === "dateRange" ? (
              <div className="stmt-daterange" aria-label="Date range selection">
                <div className="stmt-daterange__head">
                  <h2 className="stmt-daterange__title">
                    <FaCalendarAlt aria-hidden /> Date Range Selection
                  </h2>
                </div>

                <div className="stmt-daterange__presets" role="group">
                  {DATE_PRESETS.map((preset) => {
                    const isSelected = dateRangePreset === preset.value;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        className={`stmt-daterange__preset${
                          isSelected ? " is-active" : ""
                        }`}
                        style={{
                          "--preset-color": preset.color,
                        }}
                        onClick={() => handleDateRangePreset(preset.value)}
                      >
                        <i className={`fa ${preset.icon}`} aria-hidden />
                        <span>{preset.label}</span>
                        {isSelected ? (
                          <FaCheck aria-hidden className="stmt-daterange__check" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                <div className="stmt-daterange__calendar">
                  <div className="stmt-daterange__cal-nav">
                    <button
                      type="button"
                      className="stmt-daterange__nav-btn"
                      onClick={() => navigateCalendarMonth(-1)}
                      aria-label="Previous month"
                    >
                      <i className="fa fa-chevron-left" aria-hidden />
                    </button>
                    <h3 className="stmt-daterange__cal-label">
                      {calendarMonth.toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                      })}
                    </h3>
                    <button
                      type="button"
                      className="stmt-daterange__nav-btn"
                      onClick={() => navigateCalendarMonth(1)}
                      aria-label="Next month"
                    >
                      <i className="fa fa-chevron-right" aria-hidden />
                    </button>
                  </div>
                  <div className="stmt-daterange__days">
                    {generateCalendarDays.map((date) => {
                      const dateStr = formatDateLocal(date);
                      const inRange = isDateInRange(date);
                      const selected = isDateSelected(date);
                      const todayDate = isTodayDate(date);
                      const isStart = dateStr === dateFrom;
                      const isEnd = dateStr === dateTo;
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          title={date.toLocaleDateString()}
                          className={`stmt-daterange__day${
                            isStart || isEnd ? " is-endpoint" : ""
                          }${inRange ? " is-in-range" : ""}${
                            todayDate ? " is-today" : ""
                          }${selected ? " is-selected" : ""}`}
                          onClick={() => handleCalendarDateClick(date)}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="stmt-dates">
                  <div className="stmt-dates__field">
                    <span className="stmt-dates__label">Start Date</span>
                    <input
                      type="date"
                      className="stmt-dates__control"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setDateRangePreset("custom");
                      }}
                    />
                  </div>
                  <div className="stmt-dates__field">
                    <span className="stmt-dates__label">End Date</span>
                    <input
                      type="date"
                      className="stmt-dates__control"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setDateRangePreset("custom");
                      }}
                    />
                  </div>
                  <div className="stmt-dates__field">
                    <span className="stmt-dates__label">Selected</span>
                    <div
                      className={`stmt-dates__control stmt-dates__control--selected${
                        dateFrom && dateTo ? "" : " is-empty"
                      }`}
                    >
                      {dateFrom && dateTo ? (
                        <>
                          <i className="fa fa-info-circle" aria-hidden />
                          <span>
                            {formatDisplayDate(dateFrom)} –{" "}
                            {formatDisplayDate(dateTo)}
                          </span>
                        </>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {reportType === "month" ? (
              <div className="stmt-months" aria-label="Select month">
                <div className="stmt-months__head">
                  <h2 className="stmt-months__title">
                    <FaCalendarAlt aria-hidden /> Select month
                  </h2>
                </div>
                {monthOptions.length > 0 ? (
                  <div className="stmt-months__grid" role="listbox">
                    {monthOptions.map((ym) => {
                      const date = new Date(`${ym}-01T00:00:00`);
                      const monthLong = date.toLocaleString("en-GB", {
                        month: "long",
                      });
                      const isCurrent = ym === currentYm;
                      const isActive = selectedMonth === ym;
                      return (
                        <button
                          key={ym}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`stmt-month${isActive ? " is-active" : ""}${
                            isCurrent ? " is-current" : ""
                          }`}
                          onClick={() => setSelectedMonth(ym)}
                          title={monthLong}
                        >
                          {isCurrent ? (
                            <span className="stmt-month__badge">Now</span>
                          ) : null}
                          <span className="stmt-month__name">{monthLong}</span>
                          <span className="stmt-month__year">{ym}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="stmt-months__empty">
                    No months available for this session.
                  </div>
                )}
              </div>
            ) : null}
          </section>

          {loading ? (
            <div className="stmt-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <section className="stmt-summary" aria-label="Statement summary">
                <article className="stmt-card stmt-card--in">
                  <span className="stmt-card__icon" aria-hidden>
                    <FaMoneyBillWave />
                  </span>
                  <div>
                    <span className="stmt-card__label">Collection</span>
                    <strong>{formatCurrency(summary.collection_total)}</strong>
                    <em>{summary.collection_count} txn</em>
                  </div>
                </article>
                <article className="stmt-card stmt-card--cashin">
                  <span className="stmt-card__icon" aria-hidden>
                    <FaCalendarAlt />
                  </span>
                  <div>
                    <span className="stmt-card__label">Cash in</span>
                    <strong>{formatCurrency(summary.cash_in_total)}</strong>
                    <em>{summary.cash_in_count} entries</em>
                  </div>
                </article>
                <article className="stmt-card stmt-card--out">
                  <span className="stmt-card__icon" aria-hidden>
                    <FaMoneyBillWave />
                  </span>
                  <div>
                    <span className="stmt-card__label">Expense (cash out)</span>
                    <strong>{formatCurrency(summary.cash_out_total)}</strong>
                    <em>{summary.cash_out_count} entries</em>
                  </div>
                </article>
                <article className="stmt-card stmt-card--pay">
                  <span className="stmt-card__icon" aria-hidden>
                    <FaUserTie />
                  </span>
                  <div>
                    <span className="stmt-card__label">Payroll salary</span>
                    <strong>{formatCurrency(summary.payroll_total)}</strong>
                    <em>{summary.payroll_count} payments</em>
                  </div>
                </article>
                <article
                  className={`stmt-card stmt-card--net${
                    Number(summary.net_total) >= 0
                      ? " is-positive"
                      : " is-negative"
                  }`}
                >
                  <div>
                    <span className="stmt-card__label">Net position</span>
                    <strong>{formatCurrency(summary.net_total)}</strong>
                    <em>
                      In {formatCurrency(summary.inflow_total)} · Out{" "}
                      {formatCurrency(summary.outflow_total)}
                    </em>
                  </div>
                </article>
              </section>

              {samples.length > 1 ? (
                <section className="stmt-samples" aria-label="Printable samples">
                  <div className="stmt-samples__head">
                    <h2 className="stmt-samples__title">
                      <FaFileAlt aria-hidden /> Sample
                    </h2>
                  </div>
                  <div className="stmt-samples__chips" role="group">
                    {samples.map((sample, index) => {
                      const id = String(sample.sample_id || index);
                      const active =
                        String(selectedSample?.sample_id || "") ===
                        String(sample.sample_id || "");
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`stmt-sample-chip${
                            active ? " is-active" : ""
                          }`}
                          onClick={() => handleSampleSelect(sample)}
                        >
                          {active ? (
                            <FaCheck aria-hidden />
                          ) : (
                            <FaFileAlt aria-hidden />
                          )}
                          <span>{sampleDisplayName(sample, index)}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="stmt-preview" aria-label="Statement preview">
                <div className="stmt-preview__head">
                  <div className="min-w-0">
                    <h2 className="stmt-preview__title">
                      <FaFileAlt aria-hidden /> Printable statement
                    </h2>
                    <p className="stmt-preview__sub">
                      Collection · Expense · Payroll · {periodLabel}
                    </p>
                  </div>
                </div>
                <div className="stmt-preview__frame-wrap">
                  {iframeLoading ? (
                    <div className="stmt-preview__loader">
                      <MyLoader />
                    </div>
                  ) : null}
                  {fileUrl ? (
                    <Iframe
                      key={fileUrl}
                      ref={iframeRef}
                      src={fileUrl}
                      width="100%"
                      height={height}
                      className="stmt-preview__iframe"
                      id="statementReportFrame"
                      scrolling="no"
                      onLoad={() => setIframeLoading(false)}
                    />
                  ) : (
                    <div className="stmt-preview__empty">
                      {reportType === "month"
                        ? "Select a month to load the statement."
                        : reportType === "dateRange"
                        ? "Select start and end dates to load the statement."
                        : reportType === "asOnDate"
                        ? "Session start to today will load once session dates are available."
                        : "Select a period to load the statement."}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={(id, name, _status, startDate, endDate) => {
            setSessionId(id);
            setSessionName(name);
            setSessionStartDate(startDate || "");
            setSessionEndDate(endDate || "");
            setIsOpenSessionDialog(false);
          }}
        />
      ) : null}
    </div>
  );
}
